import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { Monogram } from '@/components/Monogram';
import { Spinner } from '@/components/Spinner';
import { useAuth } from '@/state/auth';
import { ApiError } from '@/api/client';

/** Экран входа в кабинет «Коллегиум». Перенесён из static/app-v2-prototype.html. */
export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const login = useAuth((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const from = (loc.state as { from?: string } | undefined)?.from || '/projects';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setError(null);
    try {
      await login({ email: email.trim(), password });
      nav(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Неверная почта или пароль');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Слишком много попыток входа. Подождите минуту и попробуйте снова');
      } else {
        setError('Не удалось войти. Проверьте соединение и попробуйте снова');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white flex flex-col items-center justify-center px-6 py-12">
      <form
        noValidate
        onSubmit={submit}
        className="w-full bg-white border border-border rounded-card"
        style={{ maxWidth: 420, padding: 40 }}
      >
        <Monogram size={48} fontSize={28} />

        <h2 className="font-heading text-ink mt-6" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Вход в кабинет
        </h2>
        <p className="text-muted mt-2" style={{ fontSize: 13 }}>
          Экспертиза проектной документации
        </p>

        <div className="mt-8">
          <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
            Эл. почта
          </label>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
              style={{ padding: '12px 16px 12px 40px', fontSize: 14 }}
            />
          </div>
        </div>

        <div className="mt-4">
          <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
            Пароль
          </label>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              id="password"
              type={showPwd ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
              style={{ padding: '12px 40px 12px 40px', fontSize: 14 }}
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
              aria-label={showPwd ? 'Скрыть пароль' : 'Показать пароль'}
            >
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="flex justify-end mt-2">
            {/* Самосброса пароля нет (SMTP не настроен). Сброс — через того, кто
                выдал доступ: администратора или заказчика проекта, который
                генерирует одноразовую ссылку сброса. Раньше здесь была мёртвая
                ссылка href="#" (ничего не делала). */}
            <button
              type="button"
              onClick={() => setForgotOpen((v) => !v)}
              className="text-xs text-muted hover:text-ink"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Не помните пароль?
            </button>
          </div>
          {forgotOpen && (
            <div className="text-xs text-muted mt-2" style={{ lineHeight: 1.5, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px' }}>
              Сброс пароля — через того, кто выдал вам доступ: администратора
              организации или заказчика проекта. Он сформирует одноразовую ссылку
              для сброса и передаст её вам.
            </div>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-btn mt-4 px-3 py-2.5"
            style={{
              background: 'rgba(239,68,68,.08)',
              border: '1px solid rgba(239,68,68,.3)',
            }}
          >
            <AlertCircle size={16} className="text-rose mt-0.5 shrink-0" />
            <span style={{ fontSize: 13, color: '#0A1628' }}>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !email.trim() || !password}
          className="w-full inline-flex items-center justify-center gap-2 rounded-btn font-semibold text-ink mt-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: '#F59E0B',
            padding: 14,
            fontSize: 16,
          }}
          onMouseEnter={(e) => { if (!busy) e.currentTarget.style.background = '#D97706'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#F59E0B'; }}
        >
          {busy ? <Spinner size={18} /> : <>Войти <LogIn size={18} /></>}
        </button>

        <div className="border-t border-border mt-8 pt-4">
          <p className="text-center text-xs text-muted">
            Доступ выдаётся администратором организации
          </p>
        </div>
      </form>

      <p className="text-center text-xs text-muted mt-6">© 2026 Коллегиум</p>
    </div>
  );
}
